import { api } from "@/lib/api";
import type { ApiResponse } from "@/types";
import { getPreferredPartnerId, removePreferredPartnerId } from "./token";
import type {
	Agent,
	AgentPartnerData,
	Partner,
	PartnerResponse,
	User,
} from "./types";

export async function fetchUserDetails(): Promise<User> {
	return api
		.get<ApiResponse<User>>("/api/v0/users/details")
		.then((r) => r.data.data);
}

/**
 * Fetch user details, returning null if not authenticated.
 * Use this for pages that need to check auth status without throwing errors.
 */
export async function fetchUserDetailsSafe(): Promise<User | null> {
	try {
		const response = await api.get<ApiResponse<User>>("/api/v0/users/details");
		return response.data.data;
	} catch {
		return null;
	}
}

export async function fetchCurrentAgent(): Promise<Agent> {
	return api
		.get<ApiResponse<Agent>>("leads/agents/current?methods=resources")
		.then((r) => r.data.data);
}

/**
 * Verify if the current user is an internal agent
 * Used for agent access flow (when partner_id is in URL)
 */
export async function verifyAgentAccess(): Promise<Agent> {
	return api
		.get<ApiResponse<Agent>>(
			"leads/agents/current?methods=children_ids,resources&include=phones",
		)
		.then((r) => r.data.data);
}

/**
 * Fetch partner data for agent access flow
 * Uses different endpoint than normal partner flow
 */
export async function fetchPartnerForAgent(
	partnerId: string,
): Promise<Partner> {
	const url = `leads/partners/${partnerId}?cache=false&methods=agent_details,default_region_canonical_name,navbar_config,partner_custom_form_id,has_whitelabel_inventory,config,draft_config`;

	const response = await api.get<ApiResponse<AgentPartnerData>>(url);
	const data = response.data.data;

	// Transform to match Partner structure expected by the app
	return {
		id: data.id,
		partner_id: data.id,
		partner: {
			id: data.id,
			name: data.name,
			canonical_name: data.canonical_name,
			is_active: true,
			parent_id: data.parent_id,
			type: data.parent_id ? "sub_partner" : "partner",
		},
		partner_details: {
			id: data.id,
			name: data.name,
			canonical_name: data.canonical_name,
			uuid: data.uuid,
			navbar_config: data.navbar_config || [],
			is_partner_custom_form_enabled: data.is_partner_custom_form_enabled,
			partner_custom_form_id: data.partner_custom_form_id,
			config: data.config,
			draft_config: data.draft_config,
		},
		parent_details: {
			id: data.parent_id ?? undefined,
			is_booking_partner: data.is_booking_partner,
		},
		// Agent access specific fields
		isAgent: true,
		is_sub_partner: Boolean(data.parent_id),
		// Agents have full access - no role restrictions
		role: "admin",
		name: data.name,
		email: "",
		created_at: "",
		updated_at: "",
		// Pass through additional data from API
		agent_details: data.agent_details,
		data: data.data,
		has_whitelabel_inventory: data.has_whitelabel_inventory,
		whitelabel_cities: data.whitelabel_cities,
		whitelabel_domain_url: data.whitelabel_domain_url,
	};
}

const PARTNERS_URL =
	"/api/v0/auth/partners/users/current?cache=false&limit=1000";

/**
 * Every partner account on this user, unfiltered. The account picker needs
 * this list: the partner_id-filtered lookup returns only the chosen account,
 * in a different shape (partner_details instead of partner).
 */
export async function fetchPartnerChoices(): Promise<PartnerResponse> {
	const response = await api.get<ApiResponse<PartnerResponse>>(PARTNERS_URL);
	return response.data.data;
}

export async function fetchAllPartners(): Promise<PartnerResponse> {
	const preferredPartner = getPreferredPartnerId();
	if (!preferredPartner) {
		return fetchPartnerChoices();
	}

	const response = await api.get<ApiResponse<PartnerResponse>>(
		`${PARTNERS_URL}&partner_id=${preferredPartner}`,
	);
	if (response.data.data?.result?.length) {
		return response.data.data;
	}

	// The cookie names a partner this user is no longer on (or one left by
	// another account on this browser), so the filtered lookup is empty. Drop
	// it and use the full list, or the user is told they have no partner.
	removePreferredPartnerId();
	return fetchPartnerChoices();
}

/**
 * Fetch full partner data with region information
 * This uses the leads/partners endpoint which returns whitelabel_cities and region data
 */
export async function fetchPartnerWithRegions(
	partnerId: number,
): Promise<Partial<Partner>> {
	const url = `leads/partners/${partnerId}?methods=default_region_canonical_name,whitelabel_cities`;

	try {
		const response = await api.get<ApiResponse<AgentPartnerData>>(url);
		const data = response.data.data;

		return {
			data: data.data,
			whitelabel_cities: data.whitelabel_cities,
		};
	} catch {
		// If the endpoint fails (e.g., partner doesn't have access), return empty
		return {};
	}
}

export async function fetchCurrentPartner(): Promise<Partner | null> {
	const response = await fetchAllPartners();
	const partners = response.result;

	if (partners.length === 0) {
		return null;
	}

	const preferredPartner = getPreferredPartnerId();

	// If preferred partner is set, find that one
	if (preferredPartner) {
		const found = partners.find((p) => `${p.partner_id}` === preferredPartner);
		return found || partners[0];
	}

	// Return first partner (will be handled by multi-partner flow)
	return partners[0];
}

export async function logout(): Promise<void> {
	await api.post("/api/v0/users/logout");
}

export async function updateAgentPause(
	agentId: number,
	pause: boolean,
): Promise<Agent> {
	return api
		.put<ApiResponse<Agent>>(`/leads/agents/${agentId}`, {
			agent: { data: { pause } },
		})
		.then((r) => r.data.data);
}
